import React, { useContext, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import styles from "./MenuBarTop.module.css";
import { getLuma } from "~/helpers/colorHelpers";
import {
  MenuBarSideListFontSize,
  MenuBarTopDescriptionFontSize,
  MenuBarTopTitleFontSize,
  NavBarSize,
} from "~/theme/models/themeModels";
import ThemeContext from "~/contexts/ThemeContext";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import UserIcon from "~/icons/UserIcon";
import LogoutIcon from "~/icons/LogoutIcon";
import { getSocialIcon } from "~/helpers/cssHelpers";
import { useOutsideClick } from "~/hooks/useOutsideClick";
import AuthContext from "~/contexts/AuthContext";
import { Link, useFetcher, useNavigate } from "@remix-run/react";
import { Site } from "~/entities/siteEntities";
import OrderContext from "~/contexts/OrderContext";
import clsx from "clsx";
import { urlGen } from "~/helpers/routesHelpers";
import HistoryIcon from "~/icons/HistoryIcon";
import CateringIcon from "~/icons/CateringIcon";
import { useCateringEntry } from "~/hooks/useCateringEntry";

interface MenuBarTopProps {
  orgId: string;
  site?: Site;
  orderId: string;
  visible: boolean;
  onClose: VoidFunction;
  openAuthDialog: VoidFunction;
}

const MenuBarTop: React.FC<MenuBarTopProps> = ({
  orgId,
  site,
  orderId,
  visible,
  onClose,
  openAuthDialog,
}) => {
  const fetcher = useFetcher();
  const { template } = useContext(ThemeContext);
  const { orderSummary } = useContext(OrderContext);
  const { authenticatedUser, isAuthEnabled, useComoAuth, openMyAccountModal } =
    useContext(AuthContext);
  const cateringEntry = useCateringEntry(site, visible);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const burgerMenuRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const el = document.querySelector(
      "[data-selector='header-burger-menu']",
    ) as HTMLDivElement | null;
    if (el) {
      burgerMenuRef.current = el;
    }
  }, []);

  useOutsideClick([wrapperRef, burgerMenuRef], onClose);

  // Styles
  const backgroundColor = template.menuBar.general.backgroundColour;
  const secondaryBackgroundColor =
    template.menuBar.general.secondaryBackgroundColour;
  const iconsColor = getLuma(backgroundColor) < 150 ? "#fff" : "#000";
  const menuItemFontSize =
    MenuBarSideListFontSize[template.menuBar.menuItem.fontSize];
  const menuItemFontColor = template.menuBar.menuItem.fontColour;
  const showIcons = template.menuBar.menuItem.showIcons;
  const iconColor = template.menuBar.menuItem.iconColour;
  const menuItems = template.menuBar.menuItem.items;
  const showSocials = template.menuBar.general.showSocials;
  const showCopyright = template.menuBar.general.showCopyright;
  const brandingSocials = template.branding.socials;
  const brandingCopyrightMessage = template.branding.copyrightMessage;
  const footerLinks = {
    terms: template.global.links.termsAndConditions,
    privacy: template.global.links.privacyPolicy,
  };
  const navBarHeight = NavBarSize[template.navBar.size];
  const image = template.menuBar.topBar.image;
  const title = template.menuBar.topBar.title;
  const titleStyles = {
    fontSize: MenuBarTopTitleFontSize[template.menuBar.topBar.titleFontSize],
    color: template.menuBar.topBar.titleFontColour,
  };
  const description = template.menuBar.topBar.description;
  const descriptionStyles = {
    fontSize:
      MenuBarTopDescriptionFontSize[
        template.menuBar.topBar.descriptionFontSize
      ],
    color: template.menuBar.topBar.descriptionFontColour,
  };

  const renderMenuItems = (column: number) => {
    const items = menuItems.filter((item) => item.column === column);

    return items.map((item) => (
      <a
        key={item.title}
        href={item.link}
        className={styles.menuItem}
        style={{ color: menuItemFontColor }}
      >
        {showIcons && (
          <div
            className={styles.menuIcon}
            style={{ backgroundColor: item.icon ? "transparent" : iconColor }}
          >
            {item.icon && (
              <img
                src={uploadcareConfig.getUrl(
                  item.icon.fileId,
                  item.icon.modifiers,
                )}
                alt={item.icon.fileName}
              />
            )}
          </div>
        )}
        <div className={styles.menuContent}>
          <h3
            className={styles.menuTitle}
            style={{ fontSize: menuItemFontSize }}
          >
            {item.title}
          </h3>
          <p className={styles.menuDescription}>{item.description}</p>
        </div>
      </a>
    ));
  };

  const renderCateringItem = () => {
    if (!cateringEntry.show) return null;
    return (
      <Link
        to={cateringEntry.href}
        className={styles.cateringItem}
        style={{ textDecoration: "none", color: menuItemFontColor }}
        onClick={onClose}
      >
        {showIcons && (
          <div className={styles.menuIcon}>
            <CateringIcon color={iconColor} />
          </div>
        )}
        <div className={styles.menuContent}>
          <h3 className={styles.menuTitle} style={{ fontSize: menuItemFontSize }}>
            {cateringEntry.label}
          </h3>
          <p className={styles.menuDescription}>{cateringEntry.description}</p>
        </div>
      </Link>
    );
  };

  const renderAuthenticatedSection = () => {
    if (!isAuthEnabled) return null;

    return authenticatedUser ? (
      <div className={styles.userItems}>
        <div
          className={styles.userMenuItem}
          style={{ color: menuItemFontColor }}
          onClick={() => useComoAuth && openMyAccountModal()}
        >
          {showIcons && (
            <div className={styles.menuIcon}>
              <UserIcon color={iconColor} />
            </div>
          )}
          <div className={styles.menuContent}>
            <h3
              className={styles.menuTitle}
              style={{ fontSize: menuItemFontSize }}
            >
              My Account
            </h3>
          </div>
        </div>

        <div
          className={styles.userMenuItem}
          style={{ color: menuItemFontColor }}
          onClick={() => {
            navigate(urlGen("Orders", null));
            onClose();
          }}
        >
          {showIcons && (
            <div className={styles.menuIcon}>
              <HistoryIcon color={iconColor} />
            </div>
          )}
          <div className={styles.menuContent}>
            <h3
              className={styles.menuTitle}
              style={{ fontSize: menuItemFontSize }}
            >
              Order History
            </h3>
          </div>
        </div>
        <div
          className={styles.userMenuItem}
          style={{ color: menuItemFontColor }}
          onClick={() => {
            const formData = new FormData();
            formData.append("orgId", orgId);
            formData.append("siteId", site?.id ?? "");
            formData.append("orderId", orderId);
            if (orderSummary && orderSummary.id) {
              formData.append("orderSummary", JSON.stringify(orderSummary));
            }
            fetcher.submit(formData, {
              method: "POST",
              action: "/auth/logout",
            });
            onClose();
          }}
        >
          {showIcons && (
            <div className={styles.menuIcon}>
              <LogoutIcon color={iconColor} />
            </div>
          )}
          <div className={styles.menuContent}>
            <h3
              className={styles.menuTitle}
              style={{ fontSize: menuItemFontSize }}
            >
              Logout
            </h3>
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.userItems}>
        <div
          className={styles.userMenuItem}
          style={{ color: menuItemFontColor }}
          onClick={() => {
            openAuthDialog();
            onClose();
          }}
        >
          {showIcons && (
            <div className={styles.menuIcon}>
              <UserIcon color={iconColor} />
            </div>
          )}
          <div className={styles.menuContent}>
            <h3
              className={styles.menuTitle}
              style={{ fontSize: menuItemFontSize }}
            >
              Login / Signup
            </h3>
          </div>
        </div>
      </div>
    );
  };

  const renderBottomSection = (className: string) => {
    return (
      <div className={clsx(styles.bottomSection, className)}>
        {renderCateringItem()}
        {renderAuthenticatedSection()}
        <div className={styles.menuContent} style={{ marginTop: "16px" }}>
          {showSocials && (
            <div className={styles.socials}>
              {brandingSocials.map((social) => (
                <a
                  key={social.url}
                  href={social.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {social.customIcon ? (
                    <img
                      src={uploadcareConfig.getUrl(
                        social.customIcon.fileId,
                        social.customIcon.modifiers,
                      )}
                      alt={social.customIcon.fileName}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={getSocialIcon(social.icon) as IconProp}
                      color={iconsColor}
                      size="lg"
                    />
                  )}
                </a>
              ))}
            </div>
          )}
          <div className={styles.footerLinks}>
            <a
              href={footerLinks.terms}
              target="_blank"
              rel="noreferrer"
              className={styles.footerLink}
              style={{
                color: menuItemFontColor,
              }}
            >
              Terms & Conditions
            </a>
            <a
              href={footerLinks.privacy}
              target="_blank"
              rel="noreferrer"
              className={styles.footerLink}
              style={{
                color: menuItemFontColor,
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    visible && (
      <div
        ref={wrapperRef}
        className={styles.menuWrapper}
        style={{
          backgroundColor,
          top: navBarHeight,
        }}
      >
        <div className={styles.menuGrid}>
          {/* Left Column */}
          <div className={styles.menuColumn}>
            {renderMenuItems(1)}
            {renderBottomSection(styles.hideInTablet)}
          </div>

          {/* Middle Column */}
          <div className={styles.menuColumn}>{renderMenuItems(2)}</div>

          {/* Right Column */}
          <div className={styles.menuColumn}>
            {renderMenuItems(3)}
            {renderBottomSection(styles.hideInDesktop)}
          </div>

          {/* Image Placeholder */}
          <div
            className={styles.imagePlaceholder}
            style={{ backgroundColor: secondaryBackgroundColor }}
          >
            {image && (
              <img
                src={uploadcareConfig.getUrl(image.fileId, image.modifiers)}
                alt={image.fileName}
              />
            )}
            <div className={styles.placeholderContent}>
              <h4 className={styles.placeholderTitle} style={titleStyles}>
                {title}
              </h4>
              <p
                className={styles.placeholderDescription}
                style={descriptionStyles}
              >
                {description}
              </p>
            </div>
          </div>
        </div>

        {showCopyright && (
          <div className={styles.copyrightSection}>
            <p className={styles.copyrightText}>{brandingCopyrightMessage}</p>
          </div>
        )}
      </div>
    )
  );
};

export default MenuBarTop;
